import React, {
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { LoadingIndicator } from 'lib/components';
import RouteLeavingGuard from 'app/pages/video/videoDetails/main/RouteLeavingGuard';
import { Button } from 'react-covideo-common';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import {
  ParagraphNormalBold600,
  SubHeading,
} from 'lib/components/styles/typography';
import { Flex } from 'lib/components/styles/layout';
import { SearchAndFolderAccess } from './SearchAndFolderAccess';
import { Divider } from 'app/pages/admin/index.styled';
import { MdClose } from 'react-icons/md';
import { useGetFolderAccessSettingsOptions } from 'lib/api/folderAccess/useGetFolderAccessSettingsOptions';
import { useAddFolderAccessSettings } from 'lib/api/folderAccess/useAddFolderAccessSettings';
import { useGetFolderAccessSettings } from 'lib/api/folderAccess/useGetFolderAccessSettings';
import { useAuth } from 'lib/context';
import { MessageAndNotification } from './MessageAndNotification';
import { ManageAccessSection } from './ManageAccessSection';
import {
  FolderAccessSettings,
  LabelOption,
  MatchParams,
  Option,
  TargetType,
} from './types';
import { ShareWithDropdown } from './ShareWithDropdown';
import { isEqual } from 'lodash';
import { ShareWithTarget } from 'lib/const/VideoPages';
import {
  AccessOptions,
  Options,
  ShareWithOptions,
} from 'lib/api/folderAccess/types';
import { useTheme } from 'styled-components/macro';
import {
  checkExistingFolderAccessSettings,
  createFolderAccessSettingFromType,
  sanitizeText,
} from 'lib/utils/functions';
import { useGetFoldersV2 } from 'lib/api/folders/getFoldersV2';
import { NotFound } from 'app/pages/notFound/NotFound';
import { useTranslation } from 'react-i18next';

export const FolderShareModal = ({
  match,
}: RouteComponentProps<MatchParams>) => {
  const { folderId, page } = match.params;
  const history = useHistory();
  const { userData } = useAuth();
  const { isResellerAdmin, resellerId, customerId } = userData;
  const { t } = useTranslation();

  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [showAccessSection, setShowAccessSection] = useState(false);
  const [shareWithDropdownOptions, setShareWithDropdownOptions] = useState<
    Option[]
  >([]);
  const [accessLevelOptions, setAccessLevelOptions] = useState<AccessOptions[]>(
    []
  );
  const [shareWithOptionDetails, setShareWithOptionDetails] = useState<
    ShareWithOptions[]
  >([]);
  const [shareWithOption, setShareWithOption] = useState({
    value: '0',
    label: '',
  } as Option);
  const [selectedEntities, setSelectedEntities] = useState([] as Option[]);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState({
    value: '0',
    label: (<></>) as React.JSX.Element,
  } as LabelOption);
  const [accessList, setAccessList] = useState([] as FolderAccessSettings[]);
  const [expandAccessSection, setExpandAccessSection] = useState(false);
  const [initialAccessList, setInitialAccessList] = useState(
    [] as FolderAccessSettings[]
  );
  const themes = useTheme();
  const skipGuardRef = useRef(false);
  const { mutateAsync: saveAccessSettings } = useAddFolderAccessSettings();
  const { refetch: refetchFolders } = useGetFoldersV2({
    params: { getShared: true },
  });
  const {
    data: folderOptions,
    isLoading,
    refetch: refetchOptions,
    error: accessOptionsError,
  } = useGetFolderAccessSettingsOptions(Number(folderId), false);

  const {
    data: existingAccess,
    refetch: refetchAccess,
    error: folderAccessError,
  } = useGetFolderAccessSettings(Number(folderId));
  const showLoader = isLoading || !folderOptions;

  useEffect(() => {
    refetchOptions();
    refetchAccess();
  }, [folderId]);

  useEffect(() => {
    if (!folderOptions) {
      return;
    }
    const accessOptions = folderOptions.accessOptions.map(option => ({
      ...option,
      name: t(option.name),
    }));
    setAccessLevelOptions(accessOptions);
    const optionDetails = (folderOptions?.shareWithOptions || []).map(
      (item: ShareWithOptions) => {
        const opts = item.options.map(option => {
          const name =
            item.targetType === TargetType.ROLE ||
            item.targetType === TargetType.ACCESS
              ? t(option.name)
              : option.name;
          return {
            ...option,
            name,
          };
        });
        return {
          ...item,
          options: opts,
          name: t(sanitizeText(item.name)),
        };
      }
    );

    setShareWithOptionDetails(optionDetails);
    const shareWithOptions = optionDetails.map((item: ShareWithOptions) => {
      return {
        value: item.targetType,
        label: item.name,
      };
    });
    setShareWithDropdownOptions(shareWithOptions);
    setShareWithOption(shareWithOptions[0]);
  }, [folderOptions]);

  useEffect(() => {
    if (!existingAccess?.length) {
      setInitialAccessList([]);
      setAccessList([]);
      return;
    }

    const translatedList = existingAccess.map(item => ({
      ...item,
      targetAutomotiveRoleName: !!item.targetAutomotiveRoleId
        ? t(item.targetAutomotiveRoleName || '')
        : item.targetAutomotiveRoleName,
      targetUserAccessName: !!item.targetUserAccess
        ? t(item.targetUserAccessName || '')
        : item.targetUserAccessName,
    }));
    setInitialAccessList(translatedList);
    setAccessList(translatedList);
    setShowAccessSection(true);
    setExpandAccessSection(true);
  }, [existingAccess]);

  useEffect(() => {
    const hasAccess = accessList.length > 0;
    setShowAccessSection(hasAccess);
    setExpandAccessSection(hasAccess);
  }, [accessList]);

  const goToHome = () => {
    history.push(`/home?folderId=${folderId}&page=${page}`);
  };

  const handleSelectShareWithOption = (
    newValue: Option,
    newOptions: Option[] = []
  ) => {
    setSelectedEntities(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const existingIds = new Set(safePrev.map(val => val.value));
      let uniqueNewSelections = newOptions.filter(
        val => !existingIds.has(val.value)
      );

      // Fallback for reseller with empty options
      if (
        ((isResellerAdmin && newValue?.value === ShareWithTarget.Reseller) ||
          (!isResellerAdmin && newValue?.value === ShareWithTarget.Customer)) &&
        uniqueNewSelections.length === 0
      ) {
        const resellerEntry = {
          value:
            newValue?.value === ShareWithTarget.Reseller
              ? resellerId || '0'
              : customerId || '0',
          label:
            sanitizeText(newValue?.label) || ShareWithTarget.EntireOrganization,
        };

        // Only add if not already in prev
        if (!existingIds.has(resellerEntry.value)) {
          uniqueNewSelections = [resellerEntry];
        }
      }

      return [...safePrev, ...uniqueNewSelections];
    });

    setShareWithOption(newValue);
  };

  const handleSaveAndShare = async () => {
    skipGuardRef.current = true;
    const dataToSend = {
      folderId: Number(folderId),
      sendNotification: !!notify,
      message: message,
      forOem: false,
      accessSettings: accessList,
    };

    await saveAccessSettings(dataToSend);
    await refetchFolders();
    goToHome();
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessage(sanitizeText(e.target.value));
  };

  const hasUnsavedChanges = useCallback(() => {
    return !isEqual(initialAccessList, accessList);
  }, [initialAccessList, accessList]);

  const assignAccessToSelected = () => {
    const selectedShareWithOption = shareWithOptionDetails.find(
      (item: ShareWithOptions) => item.targetType === shareWithOption.value
    );
    const optionsFromSelectedShareWithOption =
      selectedShareWithOption?.options || [];
    const selectedOptionIds = new Set(
      selectedEntities.map((val: Option) => Number(val.value))
    );
    const result = optionsFromSelectedShareWithOption.filter((opt: Options) =>
      selectedOptionIds.has(opt.id)
    );

    const temporaryAccessList = [...accessList];

    for (const opt of result) {
      const folderAccessSettings = createFolderAccessSettingFromType({
        targetId: opt.id,
        targetType: selectedShareWithOption?.targetType,
        targetName: sanitizeText(opt.name),
        access: selectedAccessLevel.value,
        children: opt.options || [],
      });
      const checkExistingIndex = accessList.findIndex(
        (entry: FolderAccessSettings) =>
          checkExistingFolderAccessSettings(entry, folderAccessSettings)
      );
      if (checkExistingIndex >= 0) {
        temporaryAccessList[checkExistingIndex] = {
          ...temporaryAccessList[checkExistingIndex],
          access: selectedAccessLevel.value,
          isDeleted: false,
        };
      } else {
        temporaryAccessList.push({
          ...folderAccessSettings,
          folderId: Number(folderId),
        });
      }
    }
    setAccessList(temporaryAccessList);

    setSelectedEntities([]);
  };

  if (Number(folderId) <= 0 || accessOptionsError || folderAccessError) {
    return <NotFound />;
  }

  return (
    <>
      {showLoader && <LoadingIndicator isLoading={isLoading as boolean} />}
      {!showLoader && (
        <>
          <RouteLeavingGuard
            when={true}
            stay={true}
            onConfirm={() => {}}
            onDiscard={() => goToHome()}
            navigate={_ => goToHome()}
            shouldBlockNavigation={() => {
              const shouldBlock = hasUnsavedChanges();
              return !skipGuardRef.current && shouldBlock;
            }}
            title={t('Discard changes?')}
            text={t(
              'You have unsaved changes. Leaving now will discard them. Do you want to continue?'
            )}
            confirmButtonText={t('Continue Editing')}
            discardButtonText={t('Discard Changes')}
            showDiscardIcon={false}
            showSaveIcon={false}
            fromSharedFolder={true}
          />
          <FullScreenModal
            headerLeft={
              <Flex alignItems='center'>
                <Button
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: themes.colors.black[100],
                  }}
                  icon={<MdClose size='18' />}
                  onClick={() => goToHome()}
                />
              </Flex>
            }
            headerCenter={
              <ParagraphNormalBold600>
                {t('Share Folder')}
              </ParagraphNormalBold600>
            }
            headerRight={
              <Flex justifyContent='flex-end' gap='8px'>
                <Button
                  text={t('Save & Share')}
                  disabled={!!skipGuardRef.current || !hasUnsavedChanges()}
                  onClick={() => handleSaveAndShare()}
                />
              </Flex>
            }
          >
            <Flex width='798px' maxWidth='100%' margin='0 auto'>
              <Flex justifyContent='center' alignItems='flex-start' gap='20px'>
                <SubHeading>{t('Manage Access')}</SubHeading>
                <Flex gap='20px'>
                  <ShareWithDropdown
                    shareWithOption={shareWithOption}
                    shareWithDropdownOptions={shareWithDropdownOptions}
                    selectedEntities={selectedEntities}
                    handleSelectShareWithOption={handleSelectShareWithOption}
                  />
                  <SearchAndFolderAccess
                    setSelectedValues={setSelectedEntities}
                    selectedValues={selectedEntities}
                    shareWithOption={shareWithOption}
                    accessOptionsData={accessLevelOptions}
                    shareWithOptionsData={shareWithOptionDetails}
                    setSelectedAccess={setSelectedAccessLevel}
                    selectedAccess={selectedAccessLevel}
                  />
                  <Divider
                    maxWidth='798px'
                    marginTop='4px'
                    marginBottom='4px'
                  />
                  <Button
                    style={{ width: '180px', alignSelf: 'flex-start' }}
                    onClick={() => {
                      assignAccessToSelected();
                    }}
                    text={t('Assign Access')}
                    variant='secondary'
                    disabled={!selectedEntities || !selectedEntities?.length}
                  />
                  <MessageAndNotification
                    notify={notify}
                    setNotify={setNotify}
                    message={message}
                    handleTextChange={handleTextChange}
                  />
                </Flex>
                {!!showAccessSection && (
                  <ManageAccessSection
                    accessOptions={accessLevelOptions}
                    accessList={accessList}
                    expandAccessSection={expandAccessSection}
                    setAccessList={setAccessList}
                    setExpandAccessSection={setExpandAccessSection}
                  />
                )}
              </Flex>
            </Flex>
          </FullScreenModal>
        </>
      )}
    </>
  );
};
